import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { NotePencil, Plus, Trash, ChartBar, ClockCounterClockwise, Lightning, Receipt, MagnifyingGlass, WarningCircle, CheckCircle, XCircle, FilePdf, Eye, PencilSimple, ClipboardText } from "@phosphor-icons/react";
import ShiftLogbookPanel from "@/pages/maintenance/ShiftLogbookPanel";
import { api, apiError, apiDelete } from "@/lib/api";
import ProjectBadge from "@/components/ProjectBadge";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { useConfirmDelete } from "@/components/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EVENT_KINDS = [
  { v: "PM250", label: "Preventivo 250h" },
  { v: "PM500", label: "Preventivo 500h" },
  { v: "PM1000", label: "Preventivo 1000h" },
  { v: "PM2000", label: "Preventivo 2000h" },
  { v: "correctivo", label: "Correctivo" },
  { v: "inspeccion", label: "Inspección" },
  { v: "cambio_componente", label: "Cambio de componente" },
  { v: "otro", label: "Otro" },
];

const COST_KINDS = [
  { v: "vale", label: "Vale de almacén" },
  { v: "refaccion", label: "Refacción" },
  { v: "servicio", label: "Servicio externo" },
  { v: "combustible", label: "Combustible" },
  { v: "otro", label: "Otro" },
];

export default function BitacoraTab({ equipment, canWrite, onReload }) {
  const [sub, setSub] = useState("findings"); // findings | events | costs | pareto | backlog
  return (
    <div className="space-y-4" data-testid="mnt-bitacora">
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {[
          { id: "findings", label: "Hallazgos (Planner)", icon: MagnifyingGlass },
          { id: "events", label: "Bitácora eventos", icon: NotePencil },
          { id: "shift", label: "Bitácora de turno", icon: ClipboardText },
          { id: "costs", label: "Vales / costos por equipo", icon: Receipt },
          { id: "pareto", label: "Pareto 80/20", icon: ChartBar },
          { id: "backlog", label: "Backlog de OT", icon: ClockCounterClockwise },
        ].map((s) => (
          <button
            key={s.id}
            data-testid={`mnt-bit-sub-${s.id}`}
            onClick={() => setSub(s.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 -mb-px ${
              sub === s.id ? "border-blue-900 text-blue-900" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <s.icon size={14} weight="bold" /> {s.label}
          </button>
        ))}
      </div>
      {sub === "findings" && <FindingsPanel equipment={equipment} canWrite={canWrite} onReload={onReload} />}
      {sub === "events" && <EventsPanel equipment={equipment} canWrite={canWrite} onReload={onReload} />}
      {sub === "shift" && <ShiftLogbookPanel canWrite={canWrite} />}
      {sub === "costs" && <CostsPanel equipment={equipment} canWrite={canWrite} />}
      {sub === "pareto" && <ParetoPanel />}
      {sub === "backlog" && <BacklogPanel canWrite={canWrite} onReload={onReload} />}
    </div>
  );
}

// ------------ Findings (Planner workflow) ------------
const SEV_COLORS = {
  high: { bg: "bg-red-50", border: "border-red-400", text: "text-red-800", chip: "bg-red-600 text-white" },
  med:  { bg: "bg-amber-50", border: "border-amber-400", text: "text-amber-800", chip: "bg-amber-500 text-white" },
  low:  { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-800", chip: "bg-blue-500 text-white" },
};

function FindingsPanel({ equipment, canWrite, onReload }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [scanning, setScanning] = useState(false);
  const [bulking, setBulking] = useState(false);
  const [days, setDays] = useState(7);
  const [convDlg, setConvDlg] = useState(null); // finding being converted
  const [dismissDlg, setDismissDlg] = useState(null);
  const [showLearning, setShowLearning] = useState(false);
  const [learned, setLearned] = useState([]);
  const load = () => api.get(`/maintenance/findings?status=${status}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [status]);
  useEffect(() => {
    if (showLearning) api.get("/maintenance/scan-learning").then((r) => setLearned(r.data)).catch(() => {});
  }, [showLearning]);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.post(`/maintenance/findings/scan?days=${days}`);
      const d = r.data;
      const skipped = d.skipped_learned || 0;
      if (d.created > 0) {
        toast.success(`${d.created} hallazgos creados (${d.high} altos · ${d.med} medios · ${d.low} bajos) — ${d.scanned} escaneados${skipped ? `, ${skipped} filtrados por aprendizaje` : ""}`);
      } else {
        toast.info(`Sin hallazgos nuevos — ${d.scanned} escaneados${skipped ? `, ${skipped} filtrados por aprendizaje` : ""}`);
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setScanning(false); }
  };

  const bulkConvertHigh = async () => {
    const count = counts.high;
    if (count === 0) return;
    if (!confirm(`¿Convertir los ${count} hallazgos URGENTES a OT en un solo paso?\n\nCada uno generará una OT correctivo programada para hoy.`)) return;
    setBulking(true);
    try {
      const r = await api.post("/maintenance/findings/bulk-convert", { severity: "high", kind: "correctivo" });
      toast.success(`${r.data.converted} OT(s) creadas del backlog urgente`);
      load();
      onReload?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulking(false); }
  };

  const removeLearned = async (id) => {
    if (!confirm("¿Eliminar este patrón aprendido? El scanner volverá a considerar estos comentarios.")) return;
    await api.delete(`/maintenance/scan-learning/${id}`);
    setLearned(learned.filter((x) => x.id !== id));
    toast.success("Patrón eliminado");
  };

  const counts = useMemo(() => ({
    high: rows.filter((r) => r.severity === "high").length,
    med:  rows.filter((r) => r.severity === "med").length,
    low:  rows.filter((r) => r.severity === "low").length,
  }), [rows]);

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-blue-200 bg-blue-50/70 p-3.5 text-xs text-blue-900">
        <p className="font-semibold mb-1">📋 Workflow del planeador senior</p>
        <p className="opacity-90 leading-snug">
          Las OT nacen del <b>día a día del operador</b>. Presiona <b>&quot;Escanear día a día&quot;</b> para que el sistema busque
          comentarios con fugas, ruidos, fallas o solicitudes de revisión en los reportes de turno y los convierta en <b>hallazgos abiertos</b>.
          Tú los revisas, priorizas y conviertes en OT — cada OT queda con trazabilidad al operador y turno que la originó.
        </p>
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label>Estatus</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="mnt-findings-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Abiertos</SelectItem>
              <SelectItem value="converted">Convertidos a OT</SelectItem>
              <SelectItem value="dismissed">Descartados</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Escanear últimos días</Label>
          <Input type="number" min={1} max={90} value={days} onChange={(e) => setDays(parseInt(e.target.value, 10) || 7)} className="w-24" data-testid="mnt-findings-days" />
        </div>
        {canWrite && (
          <Button onClick={scan} disabled={scanning} className="bg-blue-900 hover:bg-blue-800" data-testid="mnt-findings-scan">
            <MagnifyingGlass size={14} className="mr-1.5" weight="bold" /> {scanning ? "Escaneando…" : "Escanear día a día"}
          </Button>
        )}
        {canWrite && status === "open" && counts.high > 0 && (
          <Button onClick={bulkConvertHigh} disabled={bulking} className="bg-red-600 hover:bg-red-700" data-testid="mnt-findings-bulk-convert">
            <Lightning size={14} className="mr-1.5" weight="fill" /> {bulking ? "Creando OTs…" : `Convertir ${counts.high} urgentes a OT`}
          </Button>
        )}
        <Button variant="outline" onClick={() => setShowLearning(!showLearning)} className="border-slate-300 text-slate-600" data-testid="mnt-findings-learning-toggle">
          <NotePencil size={14} className="mr-1.5" /> {showLearning ? "Ocultar" : "Ver"} aprendizaje
        </Button>
        {status === "open" && (
          <div className="ml-auto flex gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-bold" data-testid="mnt-findings-count-high">🔴 {counts.high} altos</span>
            <span className="px-2 py-1 rounded bg-amber-100 text-amber-800 font-bold" data-testid="mnt-findings-count-med">🟡 {counts.med} medios</span>
            <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 font-bold" data-testid="mnt-findings-count-low">🔵 {counts.low} bajos</span>
          </div>
        )}
      </div>

      {showLearning && (
        <div className="rounded-lg border border-slate-300 bg-slate-50 p-3.5 space-y-2" data-testid="mnt-scan-learning">
          <div className="flex items-baseline justify-between">
            <p className="text-xs font-bold text-slate-700 uppercase tracking-widest">📚 Patrones aprendidos ({learned.length})</p>
            <p className="text-[11px] text-slate-500">Comentarios que serán ignorados por el próximo scan</p>
          </div>
          {learned.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-3 text-center">Aún no hay aprendizaje — descarta hallazgos con razón clara para entrenar al scanner.</p>
          ) : (
            <ul className="space-y-1.5">
              {learned.map((p) => (
                <li key={p.id} data-testid={`mnt-learned-${p.id}`}
                    className="flex items-center justify-between text-xs bg-white border border-slate-200 rounded px-2 py-1.5">
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-slate-700">&quot;{p.pattern}&quot;</span>
                    <span className="ml-2 text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 rounded">×{p.count}</span>
                    {p.last_reason && <span className="ml-2 text-[10px] text-slate-500 italic">motivo: {p.last_reason}</span>}
                  </div>
                  {canWrite && (
                    <button onClick={() => removeLearned(p.id)} data-testid={`mnt-learned-del-${p.id}`}
                            className="text-red-500 hover:text-red-700 shrink-0 ml-2"><Trash size={12} /></button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">
          {status === "open"
            ? "Sin hallazgos abiertos. Presiona &quot;Escanear día a día&quot; para detectar issues en el reporte de turno."
            : status === "converted" ? "Sin hallazgos convertidos a OT aún." : "Sin hallazgos descartados."}
        </div>
      ) : (
        <div className="grid gap-2">
          {rows.map((f) => {
            const sc = SEV_COLORS[f.severity] || SEV_COLORS.med;
            return (
              <div key={f.id} data-testid={`mnt-finding-${f.id}`}
                    className={`rounded-lg border-2 ${sc.border} ${sc.bg} p-3`}>
                <div className="flex items-start gap-3">
                  <span className={`text-[10px] font-bold px-2 py-1 rounded ${sc.chip} shrink-0`}>
                    {f.severity === "high" ? "URGENTE" : f.severity === "med" ? "MEDIO" : "BAJO"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-mono font-bold text-slate-900">{f.equipment_code}</span>
                      <span className="text-sm text-slate-600">{f.equipment_name}</span>
                      <span className={`text-[10px] font-bold px-1.5 rounded ${f.criticality === "A" ? "bg-red-200 text-red-900" : f.criticality === "B" ? "bg-amber-200 text-amber-900" : "bg-slate-200 text-slate-700"}`}>{f.criticality}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500">
                        {f.source === "operations" ? "Reporte de turno" : f.source === "maint_event" ? "Bitácora evento" : f.source}
                      </span>
                    </div>
                    <p className={`text-sm mt-1 ${sc.text}`}>&quot;{f.description}&quot;</p>
                    <p className="text-[11px] text-slate-500 mt-1">
                      {f.source_date} · {f.operator || "operador"} {f.area && `· ${f.area}`}
                      {f.status === "converted" && <> · <b className="text-emerald-700">✓ OT {f.converted_ot_number}</b></>}
                      {f.status === "dismissed" && <> · <b className="text-slate-500">✕ Descartado: {f.dismissed_reason}</b></>}
                    </p>
                  </div>
                  {canWrite && f.status === "open" && (
                    <div className="flex gap-1.5 shrink-0">
                      <Button size="sm" onClick={() => setConvDlg(f)} className="bg-blue-900 hover:bg-blue-800 h-8" data-testid={`mnt-finding-convert-${f.id}`}>
                        <CheckCircle size={13} weight="bold" className="mr-1" /> Crear OT
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDismissDlg(f)} className="h-8 border-slate-300" data-testid={`mnt-finding-dismiss-${f.id}`}>
                        <XCircle size={13} weight="bold" className="mr-1" /> Descartar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {convDlg && (
        <ConvertDialog finding={convDlg} onClose={() => setConvDlg(null)} onDone={() => { setConvDlg(null); load(); onReload?.(); }} />
      )}
      {dismissDlg && (
        <DismissDialog finding={dismissDlg} onClose={() => setDismissDlg(null)} onDone={() => { setDismissDlg(null); load(); }} />
      )}
    </div>
  );
}

function ConvertDialog({ finding, onClose, onDone }) {
  const defaultDate = new Date();
  if (finding.severity === "med") defaultDate.setDate(defaultDate.getDate() + 2);
  if (finding.severity === "low") defaultDate.setDate(defaultDate.getDate() + 5);
  const [f, setF] = useState({
    kind: finding.severity === "high" ? "correctivo" : "PM500",
    date: defaultDate.toISOString().slice(0, 10),
    duration_h: finding.severity === "high" ? 4 : 8,
    responsible: "",
    bay: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/maintenance/findings/${finding.id}/convert`, f);
      toast.success(`OT ${r.data.ot_number} creada`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="mnt-finding-convert-dialog">
        <DialogHeader><DialogTitle>Convertir hallazgo en OT</DialogTitle></DialogHeader>
        <div className="rounded bg-slate-50 border border-slate-200 p-3 mb-3">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Origen</p>
          <p className="text-sm text-slate-900 font-mono">{finding.equipment_code} · {finding.equipment_name}</p>
          <p className="text-xs text-slate-600 italic mt-1">&quot;{finding.description}&quot;</p>
          <p className="text-[11px] text-slate-500 mt-1">{finding.source_date} · {finding.operator}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Tipo de OT</Label>
            <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
              <SelectTrigger data-testid="mnt-conv-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="correctivo">Correctivo</SelectItem>
                <SelectItem value="PM250">PM 250h</SelectItem>
                <SelectItem value="PM500">PM 500h</SelectItem>
                <SelectItem value="PM1000">PM 1000h</SelectItem>
                <SelectItem value="PM2000">PM 2000h</SelectItem>
                <SelectItem value="inspeccion">Inspección</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Fecha programada</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="mnt-conv-date" /></div>
          <div><Label>Duración (h)</Label><Input type="number" step="0.5" value={f.duration_h} onChange={(e) => setF({ ...f, duration_h: parseFloat(e.target.value) })} data-testid="mnt-conv-dur" /></div>
          <div><Label>Bahía</Label><Input value={f.bay} onChange={(e) => setF({ ...f, bay: e.target.value })} placeholder="Bahía 1 / Superficie" data-testid="mnt-conv-bay" /></div>
          <div className="col-span-2"><Label>Responsable</Label><Input value={f.responsible} onChange={(e) => setF({ ...f, responsible: e.target.value })} placeholder="Técnico o líder" data-testid="mnt-conv-resp" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-blue-900 hover:bg-blue-800" data-testid="mnt-conv-save">
            {busy ? "…" : "Crear OT"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DismissDialog({ finding, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/maintenance/findings/${finding.id}/dismiss?reason=${encodeURIComponent(reason)}`);
      toast.info("Hallazgo descartado");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="mnt-finding-dismiss-dialog">
        <DialogHeader><DialogTitle>Descartar hallazgo</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 mb-2">&quot;{finding.description}&quot;</p>
        <Label>Razón (para retroalimentar al operador)</Label>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="ej. Ya fue atendido en OT anterior · Falso positivo · Duplicado" data-testid="mnt-dismiss-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || !reason.trim()} data-testid="mnt-dismiss-save">
            {busy ? "…" : "Descartar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------ Events ------------
function EventsPanel({ equipment, canWrite, onReload }) {
  const [events, setEvents] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [mode, setMode] = useState("create"); // "create" | "edit" | "view"
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/maintenance/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!filter.trim()) return events;
    const q = filter.toLowerCase();
    return events.filter((e) => `${e.equipment_code} ${e.equipment_name} ${e.kind} ${e.technician}`.toLowerCase().includes(q));
  }, [events, filter]);

  const openNew = () => {
    setMode("create");
    setF({
      equipment_id: equipment[0]?.id || "",
      kind: "correctivo",
      started_at: new Date().toISOString().slice(0, 16),
      ended_at: "",
      downtime_h: 0,
      technician: "",
      description: "",
      parts_used: "",
      cost_usd: 0,
    });
    setDlg(true);
  };
  const openView = (e) => { setMode("view"); setF({ ...e }); setDlg(true); };
  const openEdit = (e) => {
    setMode("edit");
    setF({
      ...e,
      started_at: (e.started_at || "").slice(0, 16),
      ended_at: (e.ended_at || "").slice(0, 16),
    });
    setDlg(true);
  };

  const save = async () => {
    if (!assertProject(f.project_id)) return;
    setSaving(true);
    try {
      const body = { ...f };
      body.started_at = new Date(body.started_at).toISOString();
      body.ended_at = body.ended_at ? new Date(body.ended_at).toISOString() : null;
      body.downtime_h = parseFloat(body.downtime_h) || 0;
      body.cost_usd = parseFloat(body.cost_usd) || 0;
      if (mode === "edit" && f.id) {
        await api.put(`/maintenance/events/${f.id}`, body);
        toast.success("Evento actualizado");
      } else {
        await api.post("/maintenance/events", body);
        toast.success("Evento registrado");
      }
      setDlg(false);
      load();
      onReload?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const confirmDelete = useConfirmDelete();
  const del = async (ev) => {
    const ok = await confirmDelete({
      title: "¿Eliminar evento de bitácora?",
      itemLabel: `${ev.equipment_code || ""} · ${ev.kind || ""} · ${(ev.started_at || "").slice(0, 16).replace("T", " ")}`,
      description: "Se registrará el motivo en el historial de auditoría minera.",
    });
    if (!ok) return;
    try {
      await apiDelete(`/maintenance/events/${ev.id}`, ok.reason);
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Input placeholder="Filtrar por equipo/técnico/tipo…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-sm" data-testid="mnt-bit-events-filter" />
        {canWrite && <Button onClick={openNew} data-testid="mnt-bit-events-new" className="ml-auto"><Plus size={16} className="mr-1" /> Registrar evento</Button>}
      </div>
      <div className="overflow-x-auto rounded border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-2 py-2 text-left">Equipo</th>
              <th className="px-2 py-2 text-left">Tipo</th>
              <th className="px-2 py-2 text-left">Inicio</th>
              <th className="px-2 py-2 text-left">Fin</th>
              <th className="px-2 py-2 text-right">Paro (h)</th>
              <th className="px-2 py-2 text-left">Técnico</th>
              <th className="px-2 py-2 text-right">Costo USD</th>
              <th className="px-2 py-2 text-right w-24">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-400 py-6 italic">Sin eventos registrados</td></tr>
            )}
            {filtered.map((e) => (
              <tr key={e.id}
                   className="border-t border-slate-100 hover:bg-blue-50/40 cursor-pointer"
                   onClick={() => openView(e)}
                   data-testid={`mnt-bit-event-${e.id}`}
                   title="Click para ver detalle">
                <td className="px-2 py-1.5"><span className="font-mono text-xs">{e.equipment_code}</span> <span className="text-slate-500">{e.equipment_name}</span> <ProjectBadge id={e.project_id} className="ml-1 align-middle" /></td>
                <td className="px-2 py-1.5"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${e.kind === "correctivo" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{e.kind}</span></td>
                <td className="px-2 py-1.5 text-xs">{(e.started_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-2 py-1.5 text-xs">{e.ended_at ? e.ended_at.slice(0, 16).replace("T", " ") : <span className="text-amber-600 font-bold">en curso</span>}</td>
                <td className="px-2 py-1.5 text-right font-mono">{e.downtime_h}</td>
                <td className="px-2 py-1.5 text-xs">{e.technician || "—"}</td>
                <td className="px-2 py-1.5 text-right font-mono">${(e.cost_usd || 0).toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right">
                  <div className="flex items-center gap-1 justify-end" onClick={(ev) => ev.stopPropagation()}>
                    <button onClick={() => openView(e)}
                             className="text-slate-500 hover:text-blue-700 p-1"
                             data-testid={`mnt-bit-event-view-${e.id}`}
                             title="Ver detalle">
                      <Eye size={14} />
                    </button>
                    {canWrite && (
                      <>
                        <button onClick={() => openEdit(e)}
                                 className="text-slate-500 hover:text-blue-700 p-1"
                                 data-testid={`mnt-bit-event-edit-${e.id}`}
                                 title="Editar">
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => del(e)}
                                 className="text-slate-400 hover:text-red-600 p-1"
                                 data-testid={`mnt-bit-event-del-${e.id}`}
                                 title="Eliminar">
                          <Trash size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-2xl" data-testid="mnt-bit-event-dialog"
                        aria-describedby="mnt-bit-event-desc">
          <DialogHeader>
            <DialogTitle>
              {mode === "view" && `Evento · ${f.equipment_code || ""} · ${f.kind || ""}`}
              {mode === "create" && "Registrar evento de mantenimiento"}
              {mode === "edit" && "Editar evento"}
            </DialogTitle>
            <p id="mnt-bit-event-desc" className="text-xs text-slate-500">
              Bitácora del equipo — trazabilidad de paros, correctivos, inspecciones y costos.
            </p>
          </DialogHeader>
          {mode === "view" ? (
            <EventDetailView f={f} onEdit={canWrite ? () => setMode("edit") : null}
                              onClose={() => setDlg(false)} />
          ) : (
            <>
            <ProjectField value={f.project_id} onChange={(v) => setF({ ...f, project_id: v })} testid="mnt-bit-event-project" />
            <EventForm f={f} setF={setF} equipment={equipment} save={save} saving={saving}
                        onClose={() => setDlg(false)} mode={mode} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EventDetailView({ f, onEdit, onClose }) {
  const parts = (f.parts_used || "").split(",").map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-4 text-sm" data-testid="mnt-bit-event-detail">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Equipo</p>
          <p className="font-mono text-blue-900 font-bold">{f.equipment_code} · {f.equipment_name}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Tipo</p>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${f.kind === "correctivo" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{f.kind}</span>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Inicio</p>
          <p className="font-mono">{(f.started_at || "").slice(0, 16).replace("T", " ")}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Fin</p>
          <p className="font-mono">{f.ended_at ? f.ended_at.slice(0, 16).replace("T", " ") : <span className="text-amber-600 font-bold">en curso</span>}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Paro</p>
          <p className="font-mono font-bold">{f.downtime_h || 0} h</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Costo</p>
          <p className="font-mono font-bold">${(f.cost_usd || 0).toLocaleString()} USD</p>
        </div>
        <div className="col-span-2">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Técnico</p>
          <p className="font-semibold">{f.technician || "—"}</p>
        </div>
        {f.description && (
          <div className="col-span-2">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Descripción</p>
            <p className="bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap">{f.description}</p>
          </div>
        )}
        {parts.length > 0 && (
          <div className="col-span-2">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Refacciones usadas ({parts.length})</p>
            <div className="flex flex-wrap gap-1">
              {parts.map((p, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-mono">{p}</span>
              ))}
            </div>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cerrar</Button>
        {onEdit && (
          <Button onClick={onEdit} className="gap-1.5 bg-blue-900 hover:bg-blue-800"
                   data-testid="mnt-bit-event-detail-edit">
            <PencilSimple size={14} weight="bold" /> Editar
          </Button>
        )}
      </DialogFooter>
    </div>
  );
}

function EventForm({ f, setF, equipment, save, saving, onClose, mode }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label>Equipo</Label>
          <Select value={f.equipment_id} onValueChange={(v) => setF({ ...f, equipment_id: v })}>
            <SelectTrigger data-testid="mnt-bit-event-eq"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
            <SelectContent>
              {equipment.map((eq) => (
                <SelectItem key={eq.id} value={eq.id}>{eq.code} · {eq.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Tipo</Label>
          <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
            <SelectTrigger data-testid="mnt-bit-event-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EVENT_KINDS.map((k) => (<SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Paro (h) <span className="text-slate-400 text-[10px]">(auto si defines inicio+fin)</span></Label>
          <Input type="number" step="0.1" value={f.downtime_h} onChange={(e) => setF({ ...f, downtime_h: e.target.value })} data-testid="mnt-bit-event-downtime" />
        </div>
        <div>
          <Label>Inicio *</Label>
          <Input type="datetime-local" value={f.started_at} onChange={(e) => setF({ ...f, started_at: e.target.value })} data-testid="mnt-bit-event-start" />
        </div>
        <div>
          <Label>Fin</Label>
          <Input type="datetime-local" value={f.ended_at} onChange={(e) => setF({ ...f, ended_at: e.target.value })} data-testid="mnt-bit-event-end" />
        </div>
        <div>
          <Label>Técnico</Label>
          <Input value={f.technician} onChange={(e) => setF({ ...f, technician: e.target.value })} data-testid="mnt-bit-event-tech" />
        </div>
        <div>
          <Label>Costo USD</Label>
          <Input type="number" step="0.01" value={f.cost_usd} onChange={(e) => setF({ ...f, cost_usd: e.target.value })} data-testid="mnt-bit-event-cost" />
        </div>
        <div className="col-span-2">
          <Label>Descripción</Label>
          <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="mnt-bit-event-desc-input" />
        </div>
        <div className="col-span-2">
          <Label>Refacciones usadas (separadas por coma)</Label>
          <Input value={f.parts_used} onChange={(e) => setF({ ...f, parts_used: e.target.value })} data-testid="mnt-bit-event-parts" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} disabled={saving || !f.equipment_id || !f.started_at} data-testid="mnt-bit-event-save"
                 className="bg-blue-900 hover:bg-blue-800">
          {saving ? "…" : (mode === "edit" ? "Guardar cambios" : "Guardar")}
        </Button>
      </DialogFooter>
    </>
  );
}

// ------------ Costs ------------
function CostsPanel({ equipment, canWrite }) {
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/maintenance/equipment-costs").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const total = rows.reduce((s, r) => s + (r.amount_usd || 0), 0);
  const byEq = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const eq = equipment.find((e) => e.id === r.equipment_id);
      const label = eq ? `${eq.code} · ${eq.name}` : "(desconocido)";
      m[label] = (m[label] || 0) + (r.amount_usd || 0);
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [rows, equipment]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, amount_usd: parseFloat(f.amount_usd) || 0 };
      await api.post("/maintenance/equipment-costs", body);
      toast.success("Vale registrado");
      setDlg(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("¿Eliminar este vale/costo?")) return;
    await api.delete(`/maintenance/equipment-costs/${id}`);
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-sm">
          <span className="text-slate-500">Total período:</span>{" "}
          <span className="font-heading font-black text-lg">${total.toLocaleString()}</span>
        </div>
        {canWrite && <Button onClick={() => { setF({ equipment_id: equipment[0]?.id || "", kind: "vale", date: new Date().toISOString().slice(0, 10), amount_usd: 0, description: "", reference: "" }); setDlg(true); }} className="ml-auto" data-testid="mnt-bit-cost-new"><Plus size={16} className="mr-1" /> Registrar vale/costo</Button>}
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {byEq.slice(0, 6).map(([label, val]) => (
          <div key={label} className="rounded border border-slate-200 bg-white p-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
            <p className="font-heading font-black text-xl">${val.toLocaleString()}</p>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-2 py-2 text-left">Fecha</th>
              <th className="px-2 py-2 text-left">Equipo</th>
              <th className="px-2 py-2 text-left">Tipo</th>
              <th className="px-2 py-2 text-right">Monto USD</th>
              <th className="px-2 py-2 text-left">Referencia</th>
              <th className="px-2 py-2 text-left">Descripción</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 italic text-slate-400">Sin vales/costos registrados</td></tr>}
            {rows.map((r) => {
              const eq = equipment.find((e) => e.id === r.equipment_id);
              return (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`mnt-bit-cost-${r.id}`}>
                  <td className="px-2 py-1.5">{r.date}</td>
                  <td className="px-2 py-1.5">{eq ? <><span className="font-mono">{eq.code}</span> {eq.name}</> : "?"}</td>
                  <td className="px-2 py-1.5"><span className="text-[10px] font-bold uppercase text-slate-600">{r.kind}</span></td>
                  <td className="px-2 py-1.5 text-right font-mono">${(r.amount_usd || 0).toLocaleString()}</td>
                  <td className="px-2 py-1.5 text-xs">{r.reference || "—"}</td>
                  <td className="px-2 py-1.5 text-xs">{r.description || "—"}</td>
                  <td className="px-2 py-1.5">{canWrite && <button className="text-red-600" onClick={() => del(r.id)}><Trash size={14} /></button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>Registrar vale / costo por equipo</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Equipo</Label>
              <Select value={f.equipment_id} onValueChange={(v) => setF({ ...f, equipment_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent>
                  {equipment.map((eq) => (<SelectItem key={eq.id} value={eq.id}>{eq.code} · {eq.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COST_KINDS.map((k) => (<SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fecha</Label>
              <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
            </div>
            <div>
              <Label>Monto USD</Label>
              <Input type="number" step="0.01" value={f.amount_usd} onChange={(e) => setF({ ...f, amount_usd: e.target.value })} data-testid="mnt-bit-cost-amount" />
            </div>
            <div>
              <Label>Referencia (nº vale / factura)</Label>
              <Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Descripción</Label>
              <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !f.equipment_id} data-testid="mnt-bit-cost-save">{saving ? "…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------ Pareto ------------
function ParetoPanel() {
  const [by, setBy] = useState("cost");
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/maintenance/pareto?by=${by}&days=${days}`); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [by, days]);

  const maxVal = data ? Math.max(...data.rows.map((r) => r[by === "cost" ? "cost_usd" : by === "downtime" ? "downtime_h" : "events"]), 1) : 1;

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label>Analizar por</Label>
          <Select value={by} onValueChange={setBy}>
            <SelectTrigger className="w-52" data-testid="mnt-bit-pareto-by"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cost">Costo USD</SelectItem>
              <SelectItem value="downtime">Horas de paro</SelectItem>
              <SelectItem value="events">Nº de eventos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Período (días)</Label>
          <Input type="number" min={7} max={365} value={days} onChange={(e) => setDays(parseInt(e.target.value, 10) || 90)} className="w-32" data-testid="mnt-bit-pareto-days" />
        </div>
        {data && (
          <div className="ml-auto text-sm text-slate-500">
            <b className="text-blue-900">{data.vital_few_count}</b> equipos concentran el 80% ·
            Total: <b>{by === "cost" ? `$${data.total.toLocaleString()}` : `${data.total}${by === "downtime" ? "h" : ""}`}</b>
          </div>
        )}
      </div>

      {loading && <p className="text-sm text-slate-400 italic">Calculando…</p>}
      {data && data.rows.length === 0 && <p className="text-sm text-slate-400 italic py-6">Sin datos en el período</p>}
      {data && (
        <div className="rounded border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Equipo</th>
                <th className="px-2 py-2 text-left">Criticidad</th>
                <th className="px-2 py-2 text-right">Valor</th>
                <th className="px-2 py-2 text-left w-56">% acum.</th>
                <th className="px-2 py-2 text-left">Barra</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, idx) => {
                const val = by === "cost" ? r.cost_usd : by === "downtime" ? r.downtime_h : r.events;
                const w = (val / maxVal) * 100;
                return (
                  <tr key={r.equipment_id} className={`border-t border-slate-100 ${r.is_vital_few ? "bg-red-50/40" : ""}`} data-testid={`mnt-bit-pareto-row-${idx}`}>
                    <td className="px-2 py-1.5 font-mono">{idx + 1}</td>
                    <td className="px-2 py-1.5"><span className="font-mono text-xs">{r.equipment_code}</span> {r.equipment_name}</td>
                    <td className="px-2 py-1.5"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${r.criticality === "A" ? "bg-red-100 text-red-700" : r.criticality === "B" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{r.criticality}</span></td>
                    <td className="px-2 py-1.5 text-right font-mono">{by === "cost" ? `$${val.toLocaleString()}` : val}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded relative overflow-hidden">
                          <div className={`absolute inset-y-0 left-0 ${r.cumulative_pct <= 80 ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${r.cumulative_pct}%` }} />
                          <div className="absolute top-0 bottom-0 border-r border-slate-500" style={{ left: "80%" }} title="Umbral 80%" />
                        </div>
                        <span className="text-xs font-mono w-12 text-right">{r.cumulative_pct}%</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="h-3 rounded bg-blue-100 overflow-hidden" style={{ width: 140 }}>
                        <div className="h-full bg-blue-900" style={{ width: `${w}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ------------ Backlog + Auto-generator + Programa semanal + OT manual ------------
function BacklogPanel({ canWrite, onReload }) {
  const [data, setData] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [equipmentFilter, setEquipmentFilter] = useState("");   // código del equipo si se pasó por handoff
  const load = () => api.get("/maintenance/orders/backlog").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/maintenance/equipment").then((r) => setEquipment(r.data)).catch(() => setEquipment([]));
    // Handoff desde FleetTab (session storage)
    try {
      const raw = sessionStorage.getItem("mnt.backlog.filter");
      if (raw) {
        const f = JSON.parse(raw);
        if (f?.code && Date.now() - (f.ts || 0) < 60_000) {
          setEquipmentFilter(f.code);
        }
        sessionStorage.removeItem("mnt.backlog.filter");
      }
    } catch (e) { /* noop */ }
  }, []);

  const autoGen = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/maintenance/orders/auto-generate");
      const generated = r.data.generated;
      if (generated > 0) {
        toast.success(`${generated} OT(s) generadas automáticamente`);
      } else {
        toast.info(r.data.message || "Sin nuevas OT necesarias");
      }
      load(); onReload?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };

  if (!data) return <p className="text-sm text-slate-400 italic">Cargando backlog…</p>;
  const s = data.stats;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 flex-1">
          <Card label="Total OT" value={s.total} testid="mnt-bit-backlog-total" />
          <Card label="En proceso" value={s.by_status.en_proceso || 0} testid="mnt-bit-backlog-proc" />
          <Card label="Programadas" value={s.by_status.programado || 0} testid="mnt-bit-backlog-prog" />
          <Card label="Criticidad A" value={s.by_criticality.A || 0} accent="red" testid="mnt-bit-backlog-crit-a" />
          <Card label="Correctivas" value={s.by_kind.correctivo || 0} accent="red" testid="mnt-bit-backlog-corr" />
        </div>
        {canWrite && (
          <>
            <Button onClick={() => setManualOpen(true)} data-testid="mnt-bit-backlog-manual-new" className="bg-blue-900 hover:bg-blue-800">
              <Plus size={16} className="mr-1" weight="bold" /> Nueva OT manual
            </Button>
            <Button variant="outline" onClick={() => setWeeklyOpen(true)} data-testid="mnt-bit-backlog-weekly-pdf" className="border-blue-900 text-blue-900 hover:bg-blue-50">
              <FilePdf size={16} className="mr-1" weight="bold" /> Programa semanal PDF
            </Button>
            <Button onClick={autoGen} disabled={generating} data-testid="mnt-bit-backlog-autogen" className="bg-orange-600 hover:bg-orange-700">
              <Lightning size={16} className="mr-1" weight="fill" /> {generating ? "Generando…" : "Auto-generar OT faltantes"}
            </Button>
          </>
        )}
      </div>

      <ManualOrderDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        equipment={equipment}
        onCreated={() => { load(); onReload?.(); }}
      />
      <WeeklyScheduleDialog open={weeklyOpen} onOpenChange={setWeeklyOpen} />

      <div className="rounded border border-slate-200 bg-white overflow-x-auto">
        {equipmentFilter && (
          <div className="px-3 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2" data-testid="mnt-bit-backlog-filter-banner">
            <span className="text-xs text-blue-900">Filtrando por equipo: <b className="font-mono">{equipmentFilter}</b></span>
            <button
              type="button"
              onClick={() => setEquipmentFilter("")}
              className="text-[11px] text-red-700 hover:underline font-bold ml-auto"
              data-testid="mnt-bit-backlog-filter-clear"
            >
              Quitar filtro ✕
            </button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-2 py-2 text-left">OT</th>
              <th className="px-2 py-2 text-left">Fecha</th>
              <th className="px-2 py-2 text-left">Equipo</th>
              <th className="px-2 py-2 text-left">Cri</th>
              <th className="px-2 py-2 text-left">Tipo</th>
              <th className="px-2 py-2 text-left">Motivo</th>
              <th className="px-2 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const rows = equipmentFilter
                ? data.orders.filter((o) => (o.equipment_code || "").toUpperCase() === equipmentFilter.toUpperCase())
                : data.orders;
              if (rows.length === 0) {
                return <tr><td colSpan={7} className="text-center py-6 italic text-slate-400">
                  {equipmentFilter ? `Sin OTs abiertas para ${equipmentFilter}` : "Backlog vacío 🎉"}
                </td></tr>;
              }
              return rows.map((o) => (
              <tr key={o.id} className="border-t border-slate-100" data-testid={`mnt-bit-backlog-row-${o.ot_number}`}>
                <td className="px-2 py-1.5 font-mono text-xs">{o.ot_number}{o.auto_generated && <span className="ml-1 text-[9px] uppercase text-orange-600 font-bold">AUTO</span>}</td>
                <td className="px-2 py-1.5">{o.date}</td>
                <td className="px-2 py-1.5"><span className="font-mono">{o.equipment_code}</span> <span className="text-slate-500">{o.equipment_name}</span></td>
                <td className="px-2 py-1.5"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${o.criticality === "A" ? "bg-red-100 text-red-700" : o.criticality === "B" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{o.criticality}</span></td>
                <td className="px-2 py-1.5"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${o.kind === "correctivo" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{o.kind}</span></td>
                <td className="px-2 py-1.5 text-xs">{o.motivo_real || "—"}</td>
                <td className="px-2 py-1.5"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${o.status === "en_proceso" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{o.status}</span></td>
              </tr>
            ));})()}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ label, value, accent, testid }) {
  const color = accent === "red" ? "text-red-700" : "text-slate-900";
  return (
    <div data-testid={testid} className="rounded border border-slate-200 bg-white p-3">
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
      <p className={`font-heading font-black text-xl ${color}`}>{value}</p>
    </div>
  );
}

// ------------ Manual OT creator ------------
const OT_KIND_OPTIONS = [
  { v: "PM250", label: "Preventivo 250h" },
  { v: "PM500", label: "Preventivo 500h" },
  { v: "PM1000", label: "Preventivo 1000h" },
  { v: "PM2000", label: "Preventivo 2000h" },
  { v: "correctivo", label: "Correctivo" },
  { v: "componente", label: "Cambio de componente" },
  { v: "inspeccion", label: "Inspección" },
  { v: "otro", label: "Otro" },
];
const OT_BAYS = ["Bahia 1", "Bahia 2", "Bahia 3", "Superficie", "Campo"];
const emptyManualOT = {
  date: new Date().toISOString().slice(0, 10),
  equipment_id: "",
  kind: "correctivo",
  motivo_real: "",
  bay: "Bahia 1",
  responsible: "",
  duration_h: 4,
  status: "programado",
};

function ManualOrderDialog({ open, onOpenChange, equipment, onCreated }) {
  const [f, setF] = useState(emptyManualOT);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => { if (open) setF(emptyManualOT); }, [open]);

  // Auto-suggest de motivos: consulta al backend cuando cambia el equipo.
  useEffect(() => {
    if (!f.equipment_id) { setSuggestions([]); return; }
    api.get(`/maintenance/equipment/${f.equipment_id}/motive-suggestions`)
      .then((r) => setSuggestions(r.data || []))
      .catch(() => setSuggestions([]));
  }, [f.equipment_id]);

  const save = async () => {
    if (!f.equipment_id) { toast.error("Selecciona un equipo"); return; }
    if (!assertProject(f.project_id)) return;
    if (!f.motivo_real || f.motivo_real.trim().length < 5) {
      toast.error("Escribe un motivo real (>5 caracteres)"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...f,
        duration_h: Number(f.duration_h) || 4,
        hh: (Number(f.duration_h) || 4) * 2,
      };
      const r = await api.post("/maintenance/orders", payload);
      toast.success(`OT ${r.data.ot_number} creada`);
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-xl" data-testid="mnt-manual-ot-dialog">
        <DialogHeader><DialogTitle>Nueva OT manual</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Equipo</Label>
            <Select value={f.equipment_id} onValueChange={(v) => setF({ ...f, equipment_id: v })}>
              <SelectTrigger data-testid="mnt-manual-ot-equipment"><SelectValue placeholder="Selecciona equipo…" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72 overflow-y-auto">
                {equipment.map((eq) => (
                  <SelectItem key={eq.id} value={eq.id}>
                    {eq.code} · {eq.name} {eq.criticality ? `(${eq.criticality})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fecha programada</Label>
            <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="mnt-manual-ot-date" />
          </div>
          <ProjectField value={f.project_id} onChange={(v) => setF({ ...f, project_id: v })} testid="mnt-manual-ot-project" />
          <div>
            <Label>Tipo de OT</Label>
            <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
              <SelectTrigger data-testid="mnt-manual-ot-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {OT_KIND_OPTIONS.map((k) => (<SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Bahía</Label>
            <Select value={f.bay} onValueChange={(v) => setF({ ...f, bay: v })}>
              <SelectTrigger data-testid="mnt-manual-ot-bay"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {OT_BAYS.map((b) => (<SelectItem key={b} value={b}>{b}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Duración (h)</Label>
            <Input type="number" min={0.5} step={0.5} value={f.duration_h} onChange={(e) => setF({ ...f, duration_h: e.target.value })} data-testid="mnt-manual-ot-duration" />
          </div>
          <div className="col-span-2">
            <Label>Responsable</Label>
            <Input value={f.responsible} onChange={(e) => setF({ ...f, responsible: e.target.value })} placeholder="Nombre del líder de bahía / mecánico" data-testid="mnt-manual-ot-responsible" />
          </div>
          <div className="col-span-2">
            <Label>Motivo / trabajo a realizar</Label>
            <Textarea rows={3} value={f.motivo_real} onChange={(e) => setF({ ...f, motivo_real: e.target.value })} placeholder="Descripción clara del trabajo…" data-testid="mnt-manual-ot-motivo" />
            {suggestions.length > 0 && (
              <div className="mt-1.5" data-testid="mnt-manual-ot-suggestions">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                  Motivos frecuentes de este equipo (últimas 20 OTs) — click para aplicar:
                </p>
                <div className="flex flex-wrap gap-1">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setF({ ...f, motivo_real: s.motivo })}
                      data-testid={`mnt-manual-ot-suggest-${i}`}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 border border-blue-300 text-blue-900 hover:bg-blue-100 max-w-full truncate"
                      title={`Usado ${s.count} vez(es) · última ${s.last_used || ""}`}
                    >
                      {s.motivo.length > 50 ? `${s.motivo.slice(0, 49)}…` : s.motivo}
                      {s.count > 1 && <span className="ml-1 text-[9px] text-blue-500 font-bold">×{s.count}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="mnt-manual-ot-save">
            {saving ? "Creando…" : "Crear OT"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------ Weekly Schedule PDF exporter ------------
function WeeklyScheduleDialog({ open, onOpenChange }) {
  // Default to Monday of current week
  const mondayOfWeek = (() => {
    const d = new Date();
    const day = d.getDay(); // 0=Sun, 1=Mon
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return d.toISOString().slice(0, 10);
  })();
  const [weekStart, setWeekStart] = useState(mondayOfWeek);
  const [supervisor, setSupervisor] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setWeekStart(mondayOfWeek); setSupervisor(""); } }, [open]);

  const go = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ week_start: weekStart, supervisor });
      const res = await api.get(`/maintenance/orders/weekly-schedule-pdf?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `programa-semanal-${weekStart}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="mnt-weekly-pdf-dialog">
        <DialogHeader><DialogTitle>Programa semanal — PDF</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Lunes de la semana</Label>
            <Input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} data-testid="mnt-weekly-pdf-date" />
          </div>
          <div>
            <Label>Supervisor (firma)</Label>
            <Input value={supervisor} onChange={(e) => setSupervisor(e.target.value)} placeholder="Nombre del supervisor" data-testid="mnt-weekly-pdf-supervisor" />
          </div>
          <p className="text-xs text-slate-500">
            Incluye todas las OT <b>programadas</b> o <b>en proceso</b> con fecha
            entre <b>{weekStart}</b> y el domingo siguiente, agrupadas por bahía.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={go} disabled={busy || !weekStart} className="bg-blue-900 hover:bg-blue-800" data-testid="mnt-weekly-pdf-download">
            <FilePdf size={14} weight="bold" className="mr-1.5" /> {busy ? "Generando…" : "Descargar PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
